export interface GroupableIndividualDocument {
  id: number;
  employeesSubmasterId: number;
  createdAt: string;
  pdfdocumentregistered: string | null;
  delivery_type?: string | null;
  delivery_reason?: string | null;
  name_usuario?: string | null;
  submasterCompanyId?: number | null;
}

export type GroupedIndividualDocument<T> = T & {
  documentRecordIds: number[];
  epiCount: number;
};

const SAME_DELIVERY_WINDOW_MS = 2 * 60 * 1000;

const normalizeGroupValue = (value: unknown) =>
  String(value ?? "")
    .trim()
    .toLocaleLowerCase("pt-BR");

const getDeliveryKey = (document: GroupableIndividualDocument) =>
  [
    document.employeesSubmasterId,
    document.submasterCompanyId ?? "",
    normalizeGroupValue(document.name_usuario),
    normalizeGroupValue(document.delivery_type),
    normalizeGroupValue(document.delivery_reason),
  ].join("|");

const hasRegisteredPdf = (document: GroupableIndividualDocument) =>
  normalizeGroupValue(document.pdfdocumentregistered).length > 0;

/**
 * O aplicativo registra um item para cada EPI, embora gere um único PDF com
 * todos os itens selecionados. O primeiro item de cada entrega possui o PDF;
 * os seguintes não. Por isso, um registro com PDF sempre inicia uma nova
 * ficha, mesmo quando foi criado poucos segundos depois da ficha anterior.
 */
export const groupIndividualDocuments = <T extends GroupableIndividualDocument>(
  documents: T[]
): GroupedIndividualDocument<T>[] => {
  const sortedDocuments = [...documents].sort((first, second) => {
    const firstTime = new Date(first.createdAt).getTime();
    const secondTime = new Date(second.createdAt).getTime();

    if (Number.isNaN(firstTime) || Number.isNaN(secondTime)) {
      return first.id - second.id;
    }

    return firstTime - secondTime || first.id - second.id;
  });

  const groups: Array<{
    key: string;
    lastCreatedAt: number;
    document: GroupedIndividualDocument<T>;
  }> = [];
  const lastGroupByKey = new Map<string, (typeof groups)[number]>();

  sortedDocuments.forEach((document) => {
    const key = getDeliveryKey(document);
    const createdAt = new Date(document.createdAt).getTime();
    const previousGroup = lastGroupByKey.get(key);
    const belongsToPreviousDelivery =
      previousGroup !== undefined &&
      !hasRegisteredPdf(document) &&
      !Number.isNaN(createdAt) &&
      !Number.isNaN(previousGroup.lastCreatedAt) &&
      createdAt - previousGroup.lastCreatedAt <= SAME_DELIVERY_WINDOW_MS;

    if (belongsToPreviousDelivery && previousGroup) {
      previousGroup.document.documentRecordIds.push(document.id);
      previousGroup.document.epiCount += 1;
      previousGroup.lastCreatedAt = createdAt;

      return;
    }

    const group = {
      key,
      lastCreatedAt: createdAt,
      document: {
        ...document,
        documentRecordIds: [document.id],
        epiCount: 1,
      },
    };

    groups.push(group);
    lastGroupByKey.set(key, group);
  });

  return groups
    .map((group) => group.document)
    .sort((first, second) => {
      const firstTime = new Date(first.createdAt).getTime();
      const secondTime = new Date(second.createdAt).getTime();

      if (Number.isNaN(firstTime) || Number.isNaN(secondTime)) {
        return second.id - first.id;
      }

      return secondTime - firstTime || second.id - first.id;
    });
};
